import { useEffect, useState } from "react"

import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/user-routes"
import { authErrorReset } from "../../LoginPage/redux/auth.actions"
import { authActions } from "../../LoginPage/redux/auth.slice"
import {
  commitAdminSecurity2FA,
  confirm2FASetup,
  generate2FASetup
} from "../../LoginPage/redux/auth.thunks"
import { AUTH_CODE_ERROR, STEP_QR, STEP_RECOVERY } from "../constants"

const ADMIN_2FA_FINISHED_KEY = "admin_2fa_finished"

export const useAdminTwoFactorSetupPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { token } = useParams()
  const fetching = useSelector((state) => state.auth.fetching)
  const error = useSelector((state) => state.auth.error)
  const accessToken = useSelector((state) => state.auth.accessToken)
  const ephemeralToken = useSelector((state) => state.auth.ephemeralToken)
  const twoFactorQrCode = useSelector((state) => state.auth.twoFactorQrCode)
  const twoFactorSecret = useSelector((state) => state.auth.twoFactorSecret)
  const twoFactorSetupToken = useSelector((state) => state.auth.twoFactorSetupToken)
  const twoFactorRecoveryCodes = useSelector((state) => state.auth.twoFactorRecoveryCodes)
  const twoFactorCommitToken = useSelector((state) => state.auth.twoFactorCommitToken)
  const [step, setStep] = useState(STEP_QR)
  const [verificationError, setVerificationError] = useState("")
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [isWaitingForCommit, setIsWaitingForCommit] = useState(false)
  const setupEphemeralToken = token || ephemeralToken

  useEffect(() => {
    document.title = "Admin 2FA Setup - Chargeback"
  }, [])

  useEffect(() => {
    if (sessionStorage.getItem(ADMIN_2FA_FINISHED_KEY) === "true" && accessToken) {
      sessionStorage.removeItem(ADMIN_2FA_FINISHED_KEY)
      navigate(RoutesPaths.alerts.all, { replace: true })
    }
  }, [accessToken, navigate])

  useEffect(() => {
    if (twoFactorRecoveryCodes?.length) {
      setStep(STEP_RECOVERY)
      return
    }

    if (isWaitingForCommit || isSuccessModalOpen) return

    setStep(STEP_QR)
  }, [isSuccessModalOpen, isWaitingForCommit, twoFactorRecoveryCodes])

  useEffect(() => {
    if (step !== STEP_RECOVERY) return

    window.history.pushState(null, "", window.location.href)

    const handlePopState = () => {
      window.history.forward()
    }

    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
  }, [step])

  useEffect(() => {
    if (!token) return

    dispatch(authActions.clear2FASetupState())
  }, [dispatch, token])

  useEffect(() => {
    if (
      setupEphemeralToken &&
      !twoFactorSetupToken &&
      !twoFactorQrCode &&
      !twoFactorRecoveryCodes?.length
    ) {
      dispatch(
        generate2FASetup({
          ephemeral_token: setupEphemeralToken
        })
      )
    }
  }, [dispatch, setupEphemeralToken, twoFactorQrCode, twoFactorRecoveryCodes, twoFactorSetupToken])

  useEffect(() => {
    if (!error) return

    if (!twoFactorSetupToken) return

    setVerificationError(AUTH_CODE_ERROR)
    dispatch(authErrorReset())
  }, [dispatch, error, twoFactorSetupToken])

  useEffect(() => {
    if (!isWaitingForCommit || !accessToken) return

    setIsWaitingForCommit(false)
    setIsSuccessModalOpen(true)
    sessionStorage.setItem(ADMIN_2FA_FINISHED_KEY, "true")
  }, [accessToken, isWaitingForCommit])

  useEffect(() => {
    if (!isWaitingForCommit || !error) return

    setIsWaitingForCommit(false)
  }, [error, isWaitingForCommit])

  const handleConfirmSetup = ({ otp, rememberDevice }) => {
    setVerificationError("")

    if (!twoFactorSetupToken) return

    dispatch(
      confirm2FASetup({
        setup_token: twoFactorSetupToken,
        otp,
        trust_device: rememberDevice
      })
    )
  }

  const handleRecoveryCodesSaved = () => {
    if (twoFactorCommitToken) {
      setIsWaitingForCommit(true)
      dispatch(
        commitAdminSecurity2FA({
          commit_token: twoFactorCommitToken
        })
      )
    }
  }

  const handleCancelRecoveryCodes = () => {
    setStep(STEP_QR)
  }

  const handleFinishSetup = () => {
    sessionStorage.removeItem(ADMIN_2FA_FINISHED_KEY)
    setIsSuccessModalOpen(false)
    dispatch(authActions.clear2FASetupState())
    navigate(RoutesPaths.alerts.all, { replace: true })
  }

  return {
    fetching,
    isSuccessModalOpen,
    step,
    twoFactorQrCode,
    twoFactorRecoveryCodes,
    twoFactorSecret,
    verificationError,
    handleCancelRecoveryCodes,
    handleConfirmSetup,
    handleFinishSetup,
    handleRecoveryCodesSaved,
    isQrStep: step === STEP_QR,
    isRecoveryStep: step === STEP_RECOVERY
  }
}
